import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import {
    MdOutlineSearch,
    MdChevronLeft,
    MdChevronRight,
    MdOutlineGroup,
    MdOutlinePersonAddAlt,
    MdOutlineRefresh,
    MdOutlineCancel,
    MdClose,
} from 'react-icons/md';
import { FormInput, useToast } from '@podus/ui';
import StatusBadge from '@/components/StatusBadge';
import EmptyState from '@/components/EmptyState';
import ConfirmModal from '@/components/ConfirmModal';
import { api, ApiError } from '@/lib/api';
import { formatDate, formatNumber, formatPercent } from '@/lib/format';
import type { AdminAmbassadorRow, AmbassadorStatus, Pagination } from '@/types';

const FILTERS: { label: string; value: AmbassadorStatus | 'ALL' }[] = [
    { label: 'All', value: 'ALL' },
    { label: 'Active', value: 'ACTIVE' },
    { label: 'Invited', value: 'INVITED' },
    { label: 'Suspended', value: 'SUSPENDED' },
];

/**
 * The single roster of the programme (FR-1 … FR-5, FR-35).
 *
 * Invitations aren't a separate entity — an invite *is* an ambassador row in the
 * INVITED state — so they live in this table rather than a parallel screen.
 * A row's lifecycle stage decides what it shows: pending invites expose Resend
 * and Revoke and have no metrics yet, accepted ambassadors show their numbers.
 */
export default function Ambassadors() {
    const toast = useToast();
    const [searchParams] = useSearchParams();

    const [rows, setRows] = useState<AdminAmbassadorRow[]>([]);
    const [pagination, setPagination] = useState<Pagination | null>(null);
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    // `?status=` seeds the filter so `/admin/invitations` can redirect straight
    // to the invited slice of this table.
    const [status, setStatus] = useState<AmbassadorStatus | 'ALL'>(() => {
        const requested = searchParams.get('status')?.toUpperCase();
        return FILTERS.some((f) => f.value === requested) ? (requested as AmbassadorStatus) : 'ALL';
    });
    const [loading, setLoading] = useState(true);

    /* Invite dialog */
    const [inviteOpen, setInviteOpen] = useState(false);
    const [email, setEmail] = useState('');
    const [name, setName] = useState('');
    const [inviting, setInviting] = useState(false);

    /* Revoke confirmation */
    const [revoking, setRevoking] = useState<AdminAmbassadorRow | null>(null);
    const [busy, setBusy] = useState(false);

    // Debounce so typing a name doesn't fire a query per keystroke.
    useEffect(() => {
        const timer = window.setTimeout(() => {
            setDebouncedSearch(search.trim());
            setPage(1);
        }, 350);
        return () => window.clearTimeout(timer);
    }, [search]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const query = new URLSearchParams({
                page: String(page),
                limit: '20',
                ...(debouncedSearch ? { search: debouncedSearch } : {}),
                ...(status !== 'ALL' ? { status } : {}),
            });
            const res = await api.get<{ data: AdminAmbassadorRow[]; pagination: Pagination }>(
                `/ambassador/admin/ambassadors?${query.toString()}`,
            );
            setRows(res.response.data);
            setPagination(res.response.pagination);
        } catch (err) {
            toast.error(err instanceof ApiError ? err.message : 'Could not load ambassadors');
        } finally {
            setLoading(false);
        }
    }, [page, debouncedSearch, status, toast]);

    useEffect(() => {
        void load();
    }, [load]);

    const sendInvite = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!email.trim()) return;

        setInviting(true);
        try {
            await api.post('/ambassador/admin/invitations', {
                email: email.trim().toLowerCase(),
                ...(name.trim() ? { name: name.trim() } : {}),
            });
            toast.success(`Invitation sent to ${email.trim().toLowerCase()}`);
            setEmail('');
            setName('');
            setInviteOpen(false);
            await load();
        } catch (err) {
            toast.error(err instanceof ApiError ? err.message : 'Could not send that invitation');
        } finally {
            setInviting(false);
        }
    };

    const resend = async (row: AdminAmbassadorRow) => {
        try {
            await api.post(`/ambassador/admin/invitations/${row.id}/resend`);
            toast.success(`A new invitation link was sent to ${row.email}`);
            await load();
        } catch (err) {
            toast.error(err instanceof ApiError ? err.message : 'Could not resend that invitation');
        }
    };

    const revoke = async () => {
        if (!revoking) return;
        setBusy(true);
        try {
            await api.delete(`/ambassador/admin/invitations/${revoking.id}`);
            toast.success('Invitation revoked');
            setRevoking(null);
            await load();
        } catch (err) {
            toast.error(err instanceof ApiError ? err.message : 'Could not revoke that invitation');
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="mx-auto max-w-6xl px-4 py-6 lg:px-8 lg:py-8">
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <h2 className="text-[22px] font-bold text-secondary">Ambassadors</h2>
                    <p className="mt-1 text-[13px] text-[#747474]">
                        Everyone in the programme, invited or active. Search by name, email or referral code.
                    </p>
                </div>

                <button
                    type="button"
                    onClick={() => setInviteOpen(true)}
                    className="flex items-center gap-2 rounded-lg bg-secondary px-5 py-2.5 text-[14px] font-semibold text-white transition-opacity hover:opacity-90"
                >
                    <MdOutlinePersonAddAlt size={18} />
                    Invite ambassador
                </button>
            </div>

            <div className="mt-5 flex flex-wrap items-center gap-3">
                <div className="flex min-w-0 flex-1 items-center gap-2 rounded-lg border border-[#E0E0E0] bg-white px-4 py-2.5 sm:max-w-sm">
                    <MdOutlineSearch size={18} className="shrink-0 text-[#9a9a9a]" />
                    <input
                        type="search"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search ambassadors"
                        aria-label="Search ambassadors"
                        className="min-w-0 flex-1 bg-transparent text-[14px] text-secondary outline-none placeholder:text-[#9a9a9a]"
                    />
                </div>

                <div className="flex items-center gap-1 rounded-lg border border-[#E0E0E0] bg-white p-1">
                    {FILTERS.map((filter) => (
                        <button
                            key={filter.value}
                            type="button"
                            onClick={() => {
                                setStatus(filter.value);
                                setPage(1);
                            }}
                            className={`rounded-md px-3 py-1.5 text-[13px] font-medium transition-colors ${
                                status === filter.value
                                    ? 'bg-secondary text-white'
                                    : 'text-[#6b6b6b] hover:bg-[#f5f5f5]'
                            }`}
                        >
                            {filter.label}
                        </button>
                    ))}
                </div>
            </div>

            <div className="mt-5">
                {loading ? (
                    <div className="flex flex-col gap-2">
                        {Array.from({ length: 5 }).map((_, index) => (
                            <div key={index} className="skeleton-shimmer h-14 w-full rounded-lg" />
                        ))}
                    </div>
                ) : rows.length === 0 ? (
                    <EmptyState
                        Icon={MdOutlineGroup}
                        title={debouncedSearch || status !== 'ALL' ? 'No ambassadors match that' : 'No ambassadors yet'}
                        description={
                            debouncedSearch || status !== 'ALL'
                                ? 'Try a different search term or status filter.'
                                : 'The programme is invite-only. Invite your first ambassador to get started.'
                        }
                        action={
                            !debouncedSearch && status === 'ALL' ? (
                                <button
                                    type="button"
                                    onClick={() => setInviteOpen(true)}
                                    className="inline-flex rounded-lg bg-secondary px-5 py-2.5 text-[14px] font-semibold text-white transition-opacity hover:opacity-90"
                                >
                                    Invite an ambassador
                                </button>
                            ) : undefined
                        }
                    />
                ) : (
                    <>
                        <div className="overflow-x-auto rounded-xl border border-[#E0E0E0] bg-white">
                            <table className="w-full min-w-[820px] text-left">
                                <thead>
                                    <tr className="border-b border-[#EEEEEE] text-[12px] uppercase tracking-wide text-[#9a9a9a]">
                                        <th scope="col" className="px-5 py-3 font-semibold">
                                            Ambassador
                                        </th>
                                        <th scope="col" className="px-5 py-3 font-semibold">
                                            Code
                                        </th>
                                        <th scope="col" className="px-5 py-3 font-semibold">
                                            Status
                                        </th>
                                        <th scope="col" className="px-5 py-3 font-semibold">
                                            Clicks
                                        </th>
                                        <th scope="col" className="px-5 py-3 font-semibold">
                                            Signups
                                        </th>
                                        <th scope="col" className="px-5 py-3 font-semibold">
                                            Conversion
                                        </th>
                                        <th scope="col" className="px-5 py-3 font-semibold">
                                            Joined
                                        </th>
                                        <th scope="col" className="px-5 py-3 text-right font-semibold">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((row) => {
                                        // A row that hasn't been accepted yet has no code and no
                                        // metrics — showing zeroes would read as poor performance
                                        // rather than "hasn't started".
                                        const isPendingInvite = row.status === 'INVITED';

                                        return (
                                            <tr key={row.id} className="border-b border-[#f5f5f5] last:border-0">
                                                <td className="px-5 py-3.5">
                                                    {isPendingInvite ? (
                                                        <p className="text-[14px] font-semibold text-secondary">
                                                            {row.name ?? '—'}
                                                        </p>
                                                    ) : (
                                                        <Link
                                                            to={`/admin/ambassadors/${row.id}`}
                                                            className="text-[14px] font-semibold text-secondary hover:text-primary"
                                                        >
                                                            {row.name ?? row.email}
                                                        </Link>
                                                    )}
                                                    <p className="text-[12px] text-[#9a9a9a]">{row.email}</p>
                                                </td>
                                                <td className="px-5 py-3.5 font-mono text-[13px] text-[#4E4D4D]">
                                                    {row.code ?? '—'}
                                                </td>
                                                <td className="px-5 py-3.5">
                                                    <StatusBadge
                                                        status={isPendingInvite ? row.inviteStatus : row.status}
                                                    />
                                                </td>
                                                <td className="px-5 py-3.5 text-[14px] tabular-nums text-[#4E4D4D]">
                                                    {isPendingInvite ? '—' : formatNumber(row.clicks)}
                                                </td>
                                                <td className="px-5 py-3.5 text-[14px] font-semibold tabular-nums text-secondary">
                                                    {isPendingInvite ? '—' : formatNumber(row.signups)}
                                                </td>
                                                <td className="px-5 py-3.5 text-[14px] tabular-nums text-[#4E4D4D]">
                                                    {isPendingInvite ? '—' : formatPercent(row.conversionRate)}
                                                </td>
                                                <td className="px-5 py-3.5 text-[14px] text-[#4E4D4D]">
                                                    {formatDate(row.joinedAt)}
                                                </td>
                                                <td className="px-5 py-3.5">
                                                    <div className="flex items-center justify-end gap-2">
                                                        {isPendingInvite ? (
                                                            <>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => resend(row)}
                                                                    className="flex items-center gap-1.5 rounded-lg border border-[#E0E0E0] px-3 py-1.5 text-[12px] font-semibold text-secondary transition-colors hover:bg-[#f5f5f5]"
                                                                >
                                                                    <MdOutlineRefresh size={15} />
                                                                    Resend
                                                                </button>
                                                                {row.inviteStatus === 'Pending' && (
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => setRevoking(row)}
                                                                        className="flex items-center gap-1.5 rounded-lg border border-red-200 px-3 py-1.5 text-[12px] font-semibold text-red-600 transition-colors hover:bg-red-50"
                                                                    >
                                                                        <MdOutlineCancel size={15} />
                                                                        Revoke
                                                                    </button>
                                                                )}
                                                            </>
                                                        ) : (
                                                            <Link
                                                                to={`/admin/ambassadors/${row.id}`}
                                                                className="rounded-lg border border-[#E0E0E0] px-3 py-1.5 text-[12px] font-semibold text-secondary transition-colors hover:bg-[#f5f5f5]"
                                                            >
                                                                View
                                                            </Link>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        {pagination && pagination.totalPages > 1 && (
                            <div className="mt-4 flex items-center justify-between">
                                <p className="text-[13px] text-[#747474]">
                                    Page {pagination.page} of {pagination.totalPages} · {pagination.total} total
                                </p>
                                <div className="flex items-center gap-2">
                                    <button
                                        type="button"
                                        title="Previous page"
                                        disabled={page <= 1}
                                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                                        className="flex h-9 w-9 items-center justify-center rounded-lg border border-[#E0E0E0] text-secondary transition-colors hover:bg-[#f5f5f5] disabled:opacity-40"
                                    >
                                        <MdChevronLeft size={18} />
                                    </button>
                                    <button
                                        type="button"
                                        title="Next page"
                                        disabled={page >= pagination.totalPages}
                                        onClick={() => setPage((p) => p + 1)}
                                        className="flex h-9 w-9 items-center justify-center rounded-lg border border-[#E0E0E0] text-secondary transition-colors hover:bg-[#f5f5f5] disabled:opacity-40"
                                    >
                                        <MdChevronRight size={18} />
                                    </button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* ── Invite dialog ── */}
            {inviteOpen && (
                <div className="fixed inset-0 z-[900] flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4">
                    <form
                        onSubmit={sendInvite}
                        role="dialog"
                        aria-modal="true"
                        aria-label="Invite an ambassador"
                        className="animate-fade-in w-full max-w-md rounded-t-2xl bg-white p-6 sm:rounded-2xl"
                    >
                        <div className="flex items-start justify-between gap-4">
                            <h3 className="text-[18px] font-bold text-secondary">Invite an ambassador</h3>
                            <button
                                type="button"
                                title="Close"
                                onClick={() => setInviteOpen(false)}
                                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-[#888] transition-colors hover:bg-[#f5f5f5]"
                            >
                                <MdClose size={18} />
                            </button>
                        </div>

                        <p className="mt-2 text-[14px] leading-6 text-[#747474]">
                            They&rsquo;ll get a single-use link that expires in 7 days. Nothing is created until they
                            accept.
                        </p>

                        <div className="mt-5 flex flex-col gap-4">
                            <FormInput
                                id="invite-email"
                                type="email"
                                title="Email"
                                placeholder="ambassador@example.com"
                                required
                                autoFocus
                                variant="white"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                            />
                            <FormInput
                                id="invite-name"
                                title="Name (optional)"
                                placeholder="Ada Obi"
                                variant="white"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                            />
                        </div>

                        <div className="mt-6 flex items-center justify-end gap-3">
                            <button
                                type="button"
                                onClick={() => setInviteOpen(false)}
                                className="rounded-lg border border-[#E0E0E0] px-4 py-2.5 text-[14px] font-semibold text-secondary transition-colors hover:bg-[#f5f5f5]"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={inviting || !email.trim()}
                                className="rounded-lg bg-secondary px-4 py-2.5 text-[14px] font-semibold text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                {inviting ? 'Sending…' : 'Send invitation'}
                            </button>
                        </div>
                    </form>
                </div>
            )}

            <ConfirmModal
                open={revoking !== null}
                title="Revoke this invitation?"
                description={`The link sent to ${revoking?.email ?? ''} stops working immediately. You can invite them again at any time.`}
                confirmLabel="Revoke invitation"
                destructive
                busy={busy}
                onConfirm={revoke}
                onClose={() => setRevoking(null)}
            />
        </div>
    );
}
